import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const JapaneseHeroSection = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Beautiful Japanese-themed images
  const backgroundImages = [
    'https://images.unsplash.com/photo-1545569341-9eb8b30979d9?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80', // Cherry blossoms
    'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80', // Traditional temple
    'https://images.unsplash.com/photo-1528164344705-47542687000d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2092&q=80', // Mount Fuji
    'https://images.unsplash.com/photo-1590559899731-a382839e5549?ixlib=rb-4.0.3&auto=format&fit=crop&w=2126&q=80', // Japanese lanterns
  ];

  // Japanese characters animation data
  const japaneseChars = ['ひ', 'ら', 'が', 'な', 'カ', 'タ', 'カ', 'ナ'];

  useEffect(() => {
    setIsVisible(true);
    
    // Change background image every 5 seconds
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % backgroundImages.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Floating characters animation
  const FloatingCharacter = ({ char, delay = 0, duration = 3 }) => (
    <motion.div
      className="absolute text-white/20 text-6xl font-bold pointer-events-none select-none"
      initial={{ 
        opacity: 0, 
        y: 100, 
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200),
        rotate: Math.random() * 360
      }}
      animate={{
        opacity: [0, 0.3, 0],
        y: -100,
        rotate: Math.random() * 360 + 180,
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        repeatDelay: Math.random() * 3,
        ease: "easeOut"
      }}
      style={{
        left: Math.random() * 90 + '%',
        top: '100%'
      }}
    >
      {char}
    </motion.div>
  );

  // Navigate functions
  const handleGetStarted = () => {
    // Navigate to hiragana page
    window.location.href = '/hiragana';
  };

  const handleLogin = () => {
    // Navigate to login page
    window.location.href = '/login';
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900">
      {/* Background Image Carousel */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentImageIndex}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 1.5 }}
        >
          <img
            src={backgroundImages[currentImageIndex]}
            alt="Japanese scenery"
            className="w-full h-full object-cover"
          />
          {/* Aesthetic overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/60 via-purple-900/50 to-pink-900/60" />
        </motion.div>
      </AnimatePresence>

      {/* Floating Japanese Characters */}
      {japaneseChars.map((char, index) => (
        <FloatingCharacter
          key={`${char}-${index}`}
          char={char}
          delay={index * 0.5}
          duration={4 + Math.random() * 2}
        />
      ))}

      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        {/* Traditional Japanese pattern overlay */}
        <motion.div
          className="absolute top-20 right-20 w-32 h-32 opacity-10"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full text-white fill-current">
            <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="2" />
            <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="1" />
            <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="1" />
            <circle cx="50" cy="50" r="10" fill="none" stroke="currentColor" strokeWidth="1" />
          </svg>
        </motion.div>

        {/* Aesthetic sun circle */}
        <motion.div
          className="absolute top-10 left-10 w-16 h-16 bg-gradient-to-br from-yellow-400/30 to-orange-400/30 rounded-full"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-4xl mx-auto">
          {/* Main Title with Animation */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 50 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mb-8"
          >
            <motion.h1
              className="text-7xl md:text-9xl font-bold text-white mb-4 relative"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Nekyo
              </span>
              {/* Glow effect */}
              <div className="absolute inset-0 text-7xl md:text-9xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent blur-lg opacity-50 -z-10">
                Nekyo
              </div>
            </motion.h1>
            
            {/* Japanese subtitle with typewriter effect */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 1 }}
              className="text-3xl md:text-4xl text-white/90 font-light tracking-wider"
            >
              <motion.span
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ delay: 2, duration: 2 }}
                className="inline-block overflow-hidden whitespace-nowrap border-r-2 border-white/50"
              >
                ねきょう
              </motion.span>
            </motion.div>
          </motion.div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 1, delay: 1 }}
            className="text-xl md:text-2xl text-white/80 mb-12 leading-relaxed"
          >
            Master the beauty of Japanese language through interactive learning
            <br />
            <span className="text-lg text-white/60">
              From Hiragana to Kanji - Your journey to fluency starts here
            </span>
          </motion.p>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          >
            {/* Get Started Button */}
            <motion.button
              onClick={handleGetStarted}
              className="group relative bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-10 rounded-full shadow-2xl border-2 border-purple-500/50 backdrop-blur-sm overflow-hidden"
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 25px 50px -12px rgba(168, 85, 247, 0.5)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Button background animation */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                whileHover={{ scale: 1.1 }}
              />
              <span className="relative z-10 text-lg">
                始める (Get Started)
              </span>
            </motion.button>

            {/* Login Button */}
            <motion.button
              onClick={handleLogin}
              className="group bg-white/10 hover:bg-white/20 text-white font-medium py-4 px-10 rounded-full border-2 border-white/30 hover:border-white/50 backdrop-blur-sm transition-all duration-300"
              whileHover={{ 
                scale: 1.05,
                backgroundColor: "rgba(255, 255, 255, 0.15)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="text-lg">既にアカウントを持っています</span>
              <br />
              <span className="text-sm opacity-80">I already have an account</span>
            </motion.button>
          </motion.div>

          {/* Feature Icons with Auto Animations */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.5, duration: 1 }}
            className="mt-16 flex justify-center space-x-12"
          >
            {[
              { icon: 'あ', label: 'Hiragana', color: 'from-pink-500 to-rose-500' },
              { icon: 'カ', label: 'Katakana', color: 'from-purple-500 to-indigo-500' },
              { icon: '漢', label: 'Kanji', color: 'from-cyan-500 to-blue-500' },
              { icon: '話', label: 'Speaking', color: 'from-emerald-500 to-teal-500' }
            ].map((item, index) => (
              <motion.div
                key={item.label}
                className="text-center"
                whileHover={{ scale: 1.1, y: -5 }}
                initial={{ opacity: 0, y: 20, rotateY: 180 }}
                animate={{ 
                  opacity: 1, 
                  y: 0, 
                  rotateY: 0,
                }}
                transition={{ 
                  delay: 2.5 + index * 0.3,
                  duration: 0.8,
                  type: "spring",
                  stiffness: 100
                }}
              >
                <motion.div
                  className={`w-16 h-16 bg-gradient-to-br ${item.color} backdrop-blur-sm rounded-2xl flex items-center justify-center mb-3 border border-white/20 shadow-lg`}
                  animate={{
                    boxShadow: [
                      "0 0 20px rgba(168, 85, 247, 0.3)",
                      "0 0 30px rgba(168, 85, 247, 0.6)",
                      "0 0 20px rgba(168, 85, 247, 0.3)"
                    ],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 3 + index * 0.5
                  }}
                >
                  <motion.span 
                    className="text-2xl font-bold text-white"
                    animate={{
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: 3.5 + index * 0.4
                    }}
                  >
                    {item.icon}
                  </motion.span>
                </motion.div>
                <motion.p 
                  className="text-white/70 text-sm font-medium"
                  animate={{
                    color: ["rgba(255,255,255,0.7)", "rgba(255,255,255,0.9)", "rgba(255,255,255,0.7)"]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 4 + index * 0.3
                  }}
                >
                  {item.label}
                </motion.p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3, duration: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-white/60 text-center"
        >
         
        </motion.div>
      </motion.div>
    </div>
  );
};

export default JapaneseHeroSection;